/**
 * Jedna karta produktu (polievka / hlavné jedlo) – zobrazuje badge, obrázok, názov, popis, alergény, cenu.
 * Používa sa v list aj grid layoute; classNamePrefix rozlišuje landscape (daily-menu-screen) vs portrait (daily-menu-portrait).
 */
import React from "react";
import { resolveLocaleValue } from "app/utils/resolveLocaleValue";
import { formatAllergens } from "../helpers/formatAllergens";
import { formatItemQuantity } from "../helpers/formatItemQuantity";
import { formatPrice } from "../helpers/formatPrice";
import MealNumberBadge from "../components/MealNumberBadge";
import GlutenFreeIcon from "../templates/template2/GlutenFreeIcon";

function isGlutenFree(item) {
  const v = item?.isGlutenFree ?? item?.glutenFree;
  return v === true || v === "true";
}

export default function ProductCard({
  item,
  locale,
  defaultLocale,
  placeholderImage,
  soldOutLabel = "",
  classNamePrefix = "daily-menu-screen",
  badgeOnImage = false,
  showDescription = true,
  showPrices = true,
}) {
  const imageUrl = item?.imageUrl || placeholderImage;
  const title = resolveLocaleValue(item?.title, locale, defaultLocale);
  const description = showDescription
    ? resolveLocaleValue(item?.description, locale, defaultLocale)
    : "";
  const priceDisplay = formatPrice(item?.price);
  const unavailable = item?.isActive === false;
  const allergensBrackets = formatAllergens(item?.allergens);
  const displayTitle = unavailable && soldOutLabel ? `${title} ${soldOutLabel}` : title;
  const prefix = classNamePrefix || "daily-menu-screen";
  const imageClass =
    `${prefix}__item-image` +
    (unavailable ? ` ${prefix}__item-image--inactive` : "");
  const titleClass =
    `${prefix}__item-title` +
    (unavailable ? ` ${prefix}__item-title--unavailable` : "");

  const badge = (item?.ordinal != null || item?.number != null) ? (
    <MealNumberBadge
      index={item?.ordinal ?? item?.number}
      unavailable={unavailable}
      classNamePrefix={prefix}
    />
  ) : null;

  // Množstvo (váha v g, inak objem v l) a gluten-free odznak. Portrait zoznam
  // ich dáva inline pred cenu (množstvo prvé, potom gluten-free); landscape/mriežka na obrázok.
  const weightDisplay = formatItemQuantity(item);
  const glutenFree = isGlutenFree(item);
  const isPortrait = prefix === "daily-menu-portrait";
  const inlineExtras = !badgeOnImage && isPortrait;
  const weightBadge = weightDisplay ? (
    <span
      className={`${prefix}__item-weight${inlineExtras ? ` ${prefix}__item-weight--inline` : ""}`}
    >
      {weightDisplay}
    </span>
  ) : null;
  const gfBadge = glutenFree ? (
    <span
      className={`${prefix}__item-gf${inlineExtras ? ` ${prefix}__item-gf--inline` : ""}`}
      title="Bezlepkové"
    >
      <GlutenFreeIcon size="100%" strokeWidth={0} />
    </span>
  ) : null;
  const hasImageOverlay = weightBadge || gfBadge;

  return (
    <div
      className={`${prefix}__item${unavailable ? ` ${prefix}__item--unavailable` : ""}`}
    >
      {badgeOnImage ? (
        <div className={`${prefix}__item-image-wrap`}>
          <img className={imageClass} src={imageUrl} alt="" />
          {badge}
          {gfBadge}
          {weightBadge}
          {showPrices && priceDisplay !== "" && (
            <span className={`${prefix}__item-price-on-image`}>{priceDisplay} €</span>
          )}
        </div>
      ) : (
        <>
          {badge}
          {!inlineExtras && hasImageOverlay ? (
            <div className={`${prefix}__item-media`}>
              <img className={imageClass} src={imageUrl} alt="" />
              {gfBadge}
              {weightBadge}
            </div>
          ) : (
            <img className={imageClass} src={imageUrl} alt="" />
          )}
        </>
      )}
      <div className={`${prefix}__item-body`}>
        <h3 className={titleClass}>
          {displayTitle}
          {allergensBrackets && (
            <span className={`${prefix}__item-allergens-inline`}> {allergensBrackets}</span>
          )}
        </h3>
        {showDescription && description && (
          <p className={`${prefix}__item-description`}>{description}</p>
        )}
      </div>
      {!badgeOnImage &&
        (inlineExtras && hasImageOverlay ? (
          <div className={`${prefix}__item-end`}>
            {weightBadge}
            {gfBadge}
            {showPrices && (
              <span className={`${prefix}__item-price`}>
                {priceDisplay !== "" ? `${priceDisplay} €` : ""}
              </span>
            )}
          </div>
        ) : (
          showPrices && (
            <span className={`${prefix}__item-price`}>
              {priceDisplay !== "" ? `${priceDisplay} €` : ""}
            </span>
          )
        ))}
    </div>
  );
}
